import * as React from 'react';
import Link from 'next/link';
import { Button, Typography, Box, Card, Grid, CardContent, useTheme, useMediaQuery } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import ScrollAnimation from './Components/ScrollAnimation';


// Define the array with pricing plan details
const pricingTable = [
    {
        title: "Standard Plan",
        price: "$17.99",
        features: [
            "30 day money back guarantee",
            "50K words/month",
            "3 team members",
            "Generate 10 AI images/month",
            "Topic/Idea Generator",
            "Blog/Website to Linkedin Post",
            "Youtube video to Linkedin Post",
            "Documents word,excel, powerpoint, pdf to Linkedin Post",
            "Hook Generator",
            "Linkedin Carousel Builder",
            "News content inspiration",
            "upload photos, video, pdf to your posts",
            "Extensive photo, video and gif library",
            "Post Strength Measurement and Post Improvements",
            "Post Formatting",
            "Unlimited post scheduling",
        ],
        buttonText: "Start Free Trial",
        isBestChoice: false,
        discountLabel: null,
        originalPrice: null,
        discountPercent: null,
        saveAmount: null,
    },
    {
        title: "Pro Plan",
        price: "$28.99",
        features: [
            "30 day money back guarantee",
            "Everything in Standard Plan",
            "125K words/month",
            "6 team members",
            "Generate 25 AI images/month",
        ],
        buttonText: "Start Free Trial",
        isBestChoice: true,
        discountLabel: null,
        originalPrice: null,
        discountPercent: null,
        saveAmount: null
    },
    {
        title: "Agency Plan",
        price: "$49",
        features: [
            "30 day money back guarantee",
            "Everything in Standard Plan",
            "250K words/month",
            "20 team members",
            "Generate 50 AI images/month",
        ],
        buttonText: "Start Free Trial",
        isBestChoice: false,
        discountLabel: null,
        originalPrice: null,
        discountPercent: null,
        saveAmount: null
    },
];


const StyledCard = styled(Card)(({ theme, isbestchoice }) => ({
    maxWidth: 345,
    borderRadius: '16px',
    boxShadow: theme.shadows[3],
    border: isbestchoice ? '2px solid #ec0c52' : 'none', // Highlight the best choice plan
    '&:hover': {
        boxShadow: theme.shadows[10],
    },
    backgroundColor: '#ffffff',
}));

const StyledButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(2),
    borderRadius: '20px',
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.primary.main,
    color: '#ffffff',
    '&:hover': {
        backgroundColor: theme.palette.primary.dark,
    },
}));


export default function Home() {
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

    // State for the switch
    const [billingCycle, setBillingCycle] = React.useState(false);

    const handleBillingCycleChange = (event) => {
        setBillingCycle(event.target.checked);
    };

    return (
        <div id="pricing">
            <Box sx={{ py: 4, textAlign: 'center', pt: 15 }}>
                <ScrollAnimation animateIn="flipInX" delay={0.3}>
                    <Typography variant="h3" sx={{
                        color: '#000135',
                        fontSize: '44px',
                        fontWeight: 700,
                        lineHeight: 1.13,
                        letterSpacing: '-1.76px',
                    }}>
                        Personalized Pricing Plans
                    </Typography>
                    <Typography variant="h3" sx={{
                        color: '#000135',
                        fontSize: '44px',
                        fontWeight: 700,
                        lineHeight: 1.13,
                        letterSpacing: '-1.76px',
                        mb: 8,
                    }}>
                        Suited to You.
                    </Typography>
                </ScrollAnimation>
                <Box
                    sx={{
                        backgroundColor: '#fef3c7',
                        color: '#92400e',
                        p: 3,
                        borderRadius: '12px',
                        border: '2px solid #f59e0b',
                        mb: 6,
                        mx: 'auto',
                        maxWidth: 640,
                        textAlign: 'center',
                        boxShadow: '0px 4px 10px rgba(252, 211, 77, 0.4)',
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#b45309' }}>
                        🔥 Lifetime Deal — Pay Once, Use Forever
                    </Typography>

                    <Typography variant="body1" sx={{ mt: 1.5, fontWeight: 500, color: '#78350f' }}>
                        For a limited time, get **lifetime access** to Scriptu your AI assistant for LinkedIn content — for a single one-time payment.
                        No monthly fees. No subscriptions. Ever.
                    </Typography>

                    <Typography variant="body2" sx={{ mt: 1, color: '#92400e', fontStyle: 'italic' }}>
                        ⏳ This offer expires soon and won’t come back.
                    </Typography>

                    <Link
                        href="/ltd?source=site"
                        style={{
                            display: 'inline-block',
                            marginTop: '16px',
                            fontWeight: 700,
                            backgroundColor: '#f59e0b',
                            color: '#fff',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            textDecoration: 'none',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                        }}
                    >
                        🚀 Grab the Lifetime Deal Now →
                    </Link>
                </Box>


                <ScrollAnimation animateIn="fadeInLeft" delay={0.3}>
                    <Grid container spacing={2} justifyContent="center">
                        {pricingTable.map((plan, index) => (
                            <Grid item key={index}>

                                <motion.div
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <StyledCard isbestchoice={plan.isBestChoice ? 1 : 0}>
                                        {plan.isBestChoice && (
                                            <Box sx={{ backgroundColor: '#ec0c52', color: '#ffffff', p: 1, position: 'relative', top: -10, m: '0 50px', borderRadius: '16px' }}>
                                                <Typography variant="caption" >
                                                    BEST CHOICE
                                                </Typography>
                                            </Box>
                                        )}
                                        <CardContent>
                                            <Typography variant="h5" component="h3" sx={{ color: '#ec0c52', fontSize: '20px', fontWeight: 600, mb: '15px', mt: '20px' }}>
                                                {plan.title}
                                            </Typography>
                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 1 }}>
                                                {plan.originalPrice && (
                                                    <Typography variant="body1" color="textSecondary" sx={{ textDecoration: 'line-through', fontSize: '18px', fontWeight: 500, letterSpacing: '-0.72px' }}>
                                                        {plan.originalPrice}
                                                    </Typography>
                                                )}

                                                {plan.price === "Free" ? (
                                                    <Typography variant="h4" sx={{ color: '#1B2A52', fontSize: '40px', fontWeight: 700, letterSpacing: '-1.6px' }}>
                                                        5 Days
                                                    </Typography>
                                                ) : (
                                                    <>
                                                        <Typography variant="h4" sx={{ color: '#1B2A52', fontSize: '40px', fontWeight: 700, letterSpacing: '-1.6px' }}>
                                                            {plan.price}
                                                        </Typography>
                                                        <Typography variant="caption" display="block" sx={{
                                                            color: '#6A738B',
                                                            fontSize: '14px',
                                                            fontWeight: 600,
                                                            lineHeight: '25px',
                                                            textAlign: 'center',
                                                            marginBottom: '15px'
                                                        }}>
                                                            Per Month
                                                        </Typography>
                                                    </>
                                                )}


                                            </Box>



                                            {plan.saveAmount && (
                                                <Typography variant="caption" display="block" sx={{
                                                    color: '#ec0c52',
                                                    textAlign: 'center',
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    lineHeight: '22px',
                                                    letterSpacing: '-0.39px',
                                                    ml: 5,
                                                    mr: 5,

                                                    background: 'rgba(125, 37, 236, 0.1)',
                                                    height: '22px',
                                                    padding: '0 20px',
                                                    mb: '35px'
                                                }}>
                                                    {plan.saveAmount}
                                                </Typography>
                                            )}

                                            <Link href="/app/login" underline="none" hover="none">
                                                <StyledButton variant="contained" sx={{
                                                    mb: 3,
                                                    background: '#ec0c52',
                                                    borderRadius: '8px',
                                                    height: '40px',
                                                    lineHeight: '40px',
                                                    fontSize: '14px',
                                                    fontWeight: 600,
                                                    color: '#ffffff',
                                                    textTransform: 'none',
                                                    padding: '0 20px',
                                                    boxShadow: '0px 2px 4px 0px rgba(27, 42, 82, 0.2)',
                                                    '&:hover': {
                                                        background: '#ec0c52',
                                                    }

                                                }}>
                                                    {plan.buttonText}
                                                </StyledButton>
                                            </Link>

                                            <Box sx={{ 
                                                flexGrow: 1, 
                                                display: 'flex', 
                                                justifyContent: 'center', 
                                                backgroundColor: '#f8f9fa',
                                                alignItems: 'center', 
                                                flexDirection: 'column', 
                                                mb: 2,
                                                border: '1px solid #ec0c52',
                                                borderRadius: '8px',
                                                }}>
                                                <Typography variant="caption" sx={{ color: '#ec0c52', mt: 1 }}>
                                                    Prefer one-time pricing?
                                                </Typography>
                                                <Link href="/ltd?source=site" style={{ fontWeight: 600 }}>
                                                    <Button
                                                        variant="text"
                                                        sx={{
                                                            color: '#F44336',
                                                            textDecoration: 'underline',
                                                            textTransform: 'none',
                                                            fontSize: '14px',
                                                            fontWeight: 600,
                                                            ml: 1,
                                                            '&:hover': {
                                                                textDecoration: 'underline',
                                                            },
                                                        }}
                                                    >
                                                        Get Lifetime Deal!
                                                    </Button>
                                                </Link>
                                            </Box>

                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, p: 2 }}>
                                                {plan.features.map((feature, featureIndex) => (
                                                    <Box key={featureIndex} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                        <CheckIcon sx={{ color: '#ec0c52', fontSize: '20px', alignSelf: 'start' }} />
                                                        <Typography variant="body2" sx={{
                                                            color: '#6A738B',
                                                            fontSize: '14px',
                                                            fontWeight: 500,
                                                            lineHeight: '20px',
                                                            textAlign: 'left',
                                                            flexGrow: 1, // Ensures the Typography can grow to fill the space, helping with alignment.
                                                        }}>
                                                            {feature}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </CardContent>
                                    </StyledCard>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </ScrollAnimation>
            </Box>


        </div>
    );
}
